/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Ürün görünen adı: Core `GET …/api/apps` (`label` / `name`); yedek: locale `fallback`.
 */

import { useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApps, pickLocalizedAppLabel } from '../context/AppsContext';
import { getApplicationCodeForApi } from '../config/app.config';

export type UseProductBrandingOptions = {
  /** Örn. react-i18next: `i18n.language` — verilmezse AuthContext dili kullanılır */
  language?: string;
  /** Varsayılan true — `QubePro {ürün adı}`; yazım registry / locale ile aynı (baş harf büyük vb.) */
  updateDocumentTitle?: boolean;
};

function langKeyFrom(raw: string | undefined): string {
  return String(raw ?? 'en')
    .trim()
    .split('-')[0]
    .toLowerCase();
}

/**
 * `APPLICATION_CODE` ile merkezi registry eşlemesi; yoksa `fallback` (locale).
 */
export function useProductBranding(
  fallback: string,
  options?: UseProductBrandingOptions,
): { productTitle: string } {
  const { language: authLanguage } = useAuth();
  const { apps, mainApps } = useApps();
  const appCode = getApplicationCodeForApi();

  const langKey = langKeyFrom(options?.language ?? authLanguage);
  const fb = (fallback || '').trim() || appCode;

  const productTitle = useMemo(() => {
    const fromFlat = apps.find((a) => a.code === appCode);
    const fromTree = mainApps.find((a) => a.code === appCode);
    const app = fromFlat ?? fromTree;
    const resolved = pickLocalizedAppLabel(app, langKey);
    if (resolved) return resolved;
    if (fb) return fb;
    return appCode;
  }, [apps, mainApps, appCode, langKey, fb]);

  useEffect(() => {
    if (options?.updateDocumentTitle === false) {
      return;
    }
    const title = productTitle.trim();
    document.title = /^QubePro(?:\s|$)/iu.test(title) ? title : `QubePro ${title}`;
  }, [productTitle, options?.updateDocumentTitle]);

  return { productTitle };
}
